import { z } from 'zod';
/**
 * Corps des endpoints de transfert d'agence (devis et dossier). Un admin peut
 * réaffecter une affaire à l'autre agence à tout moment.
 */
export const updateAgencySchema = z.object({
    agency: z.enum(['MRS', 'RUN']),
});
/** Code IATA de l'aéroport Roland-Garros : la seule origine rattachée à l'agence Réunion. */
const REUNION_DEPARTURE_CODE = 'RUN';
/**
 * Permission d'écriture sur le planning, par agence.
 *
 * Le calendrier se lit sans permission — il en a toujours été ainsi — mais
 * assigner un créneau, coller une sélection ou créer un modèle demande d'être
 * gestionnaire *de cette agence-là*. Deux permissions symétriques et non un
 * « gestionnaire » unique borné à sa propre agence : quelqu'un doit pouvoir
 * tenir les deux plannings sans être SUPERADMIN.
 *
 * Vit dans les contracts et non côté serveur : le back-office s'en sert pour
 * décider quels boutons il montre, et les deux réponses doivent être la même.
 */
export const PLANNING_PERMISSION_BY_AGENCY = {
    MRS: 'PLANNING_MRS',
    RUN: 'PLANNING_RUN',
};
/**
 * Un agent peut-il écrire sur le planning de cette agence ? Les SUPERADMIN
 * passent partout, comme sur le reste du back-office.
 */
export function canManagePlanningForAgency(admin, agency) {
    if (!admin)
        return false;
    if (admin.role === 'SUPERADMIN')
        return true;
    return (admin.permissions ?? []).includes(PLANNING_PERMISSION_BY_AGENCY[agency]);
}
/**
 * Expéditeur des mails transactionnels, par agence : un client doit recevoir ses
 * documents de l'agence qui suit son dossier, et pouvoir répondre directement au
 * bon interlocuteur — l'envoi pose donc ce couple à la fois en `from` et en
 * `reply-to` (cf. `BrevoService.sendTemplateEmail` : sur un envoi templaté, un
 * `reply-to` absent est repris du template, pas du `from`).
 *
 * Les deux adresses sont authentifiées (SPF/DKIM) côté Brevo. `MRS` reprend à
 * l'identique `BREVO_SENDER_EMAIL` / `BREVO_SENDER_NAME` — qui est aussi
 * l'expéditeur configuré sur *tous* les templates Brevo. Rien ne change donc
 * pour Marseille : seuls les envois Réunion basculent réellement d'adresse.
 *
 * Sert aussi de source aux coordonnées de contact affichées sur le site.
 */
// `as const satisfies` plutôt qu'une annotation : la forme reste vérifiée, mais
// les adresses gardent leur type littéral — ce qui permet d'en dériver l'enum
// zod qui verrouille l'expéditeur côté API.
export const AGENCY_SENDERS = {
    MRS: { email: 'contact@massiliavoyages.fr', name: 'Massilia Voyages' },
    RUN: { email: 'contact-reunion@massiliavoyages.fr', name: 'Massilia Voyages Réunion' },
};
/**
 * Téléphone de contact, par agence. Vit ici et non côté site : les mails
 * transactionnels en ont besoin eux aussi, et un client Réunion qui reçoit le
 * numéro de Marseille appelle dans le vide.
 */
export const AGENCY_PHONES = {
    MRS: '04 91 91 06 98',
    RUN: '+262262817878',
};
/**
 * Lien « laisser un avis » de la fiche Google de chaque agence, construit sur le
 * Place ID : il ouvre directement le formulaire de notation, là où une URL Maps
 * ou de recherche n'ouvre que la fiche — et porte des jetons de session.
 */
export const AGENCY_GOOGLE_REVIEW_URLS = {
    MRS: 'https://search.google.com/local/writereview?placeid=ChIJo2i8aJXAyRIRCcZNtLYtbYE',
    RUN: 'https://search.google.com/local/writereview?placeid=ChIJ4zNbwa2HgiERl4kR6CFWb5o',
};
/** Les seules adresses d'expédition acceptées : voir {@link agencyOfSenderEmail}. */
export const AGENCY_SENDER_EMAILS = [AGENCY_SENDERS.MRS.email, AGENCY_SENDERS.RUN.email];
/**
 * Agence correspondant à une adresse d'expédition, `undefined` si l'adresse
 * n'est pas l'une des nôtres.
 *
 * Les expéditeurs sont volontairement une liste fermée et non une saisie libre :
 * un `from` arbitraire accepté par l'API ferait du backoffice un relais
 * d'usurpation d'identité.
 */
export function agencyOfSenderEmail(email) {
    return Object.keys(AGENCY_SENDERS).find((agency) => AGENCY_SENDERS[agency].email === email);
}
/**
 * Heuristique historique de rattachement : un voyage au départ de La Réunion
 * relève de l'agence Réunion, tout le reste de Marseille.
 *
 * Elle ne sert plus qu'à *poser* l'agence (création côté client, backfill) :
 * une fois la colonne renseignée, c'est elle qui fait foi — un admin peut avoir
 * transféré le devis ou le dossier à l'autre agence.
 */
export function resolveAgencyFromDeparture(departureLocation) {
    return departureLocation === REUNION_DEPARTURE_CODE ? 'RUN' : 'MRS';
}
/**
 * Agence à poser sur un devis ou un dossier qui vient d'être créé.
 *
 * L'agence de l'admin qui crée l'emporte : c'est lui qui suivra l'affaire, quel
 * que soit le lieu de départ. À défaut d'admin (création autonome par un client
 * sur le site), on retombe sur {@link resolveAgencyFromDeparture}.
 */
export function resolveAgencyOnCreation({ adminAgency, departureLocation, }) {
    return adminAgency ?? resolveAgencyFromDeparture(departureLocation);
}
/**
 * Expéditeur d'un mail de dossier ou de devis : celui de l'agence propriétaire.
 */
export function resolveAgencySender(agency) {
    return AGENCY_SENDERS[agency];
}
